import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, MoreVertical, Pencil, Trash2, MapPin, Search, ArrowUpDown, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSortableData } from '../hooks/useSortableData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to export CSV with auth token
const exportWithAuth = async (endpoint, filename) => {
  const token = localStorage.getItem('token');
  try {
    const response = await fetch(`${API}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    
    // Detect Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // For IE/Edge legacy
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return;
    }
    
    // For Safari - use FileReader to convert to data URL
    if (isSafari) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const link = document.createElement('a');
        link.href = reader.result;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 250);
      };
      reader.readAsDataURL(blob);
      return;
    }
    
    // For Chrome, Firefox, Edge (Chromium)
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 250);
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const { canManageLocations } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', description: '' });
    setEditingLocation(null);
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name,
        address: location.address || '',
        description: location.description || ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await axios.put(`${API}/locations/${editingLocation.id}`, formData);
        toast.success('Location updated successfully');
      } else {
        await axios.post(`${API}/locations`, formData);
        toast.success('Location created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/locations/${locationToDelete.id}`);
      toast.success('Location deleted successfully');
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  // Filter locations by search term
  const searchFilteredLocations = locations.filter(location => {
    const matchesSearch = location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (location.address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (location.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Apply sorting using the custom hook
  const { sortedData: filteredLocations, sortBy, setSortBy, sortOptions } = useSortableData(searchFilteredLocations);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Locations</h1>
          <p className="text-slate-500 mt-1">Manage your storage locations</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              const params = new URLSearchParams();
              if (searchTerm) params.append('search', searchTerm);
              try {
                await exportWithAuth(`/locations/export?${params.toString()}`, 'locations_export.csv');
                toast.success('Locations exported successfully');
              } catch (error) {
                toast.error('Failed to export locations');
              }
            }}
            className="rounded-sm"
            data-testid="export-locations-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {canManageLocations() && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="add-location-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </Button>
          )}
        </div>
      </div>

      {/* Search and Sort */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="locations-search-input"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-40 rounded-sm" data-testid="sort-select">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-0">
          {filteredLocations.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created</TableHead>
                  {canManageLocations() && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLocations.map(location => (
                  <TableRow key={location.id} data-testid={`location-row-${location.id}`}>
                    <TableCell className="font-semibold text-slate-900">{location.name}</TableCell>
                    <TableCell className="text-slate-600">{location.address || '-'}</TableCell>
                    <TableCell className="text-slate-600 max-w-xs truncate">
                      {location.description || '-'}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {new Date(location.created_at).toLocaleDateString()}
                    </TableCell>
                    {canManageLocations() && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog(location)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setLocationToDelete(location);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <MapPin className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No locations found</p>
              <p className="text-sm">{searchTerm ? 'Try adjusting your search' : 'Create your first location'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">
              {editingLocation ? 'Edit Location' : 'Add New Location'}
            </DialogTitle>
            <DialogDescription>
              {editingLocation ? 'Update location details' : 'Create a new storage location'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-sm"
                data-testid="location-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="rounded-sm"
                data-testid="location-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-sm"
                rows={3}
                data-testid="location-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="location-submit-btn">
                {editingLocation ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-location-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-red-600">Delete Location</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{locationToDelete?.name}"? Locations with parts or tools cannot be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm" data-testid="confirm-delete-location-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Locations;
